import { useState } from 'react';
import { api } from '../lib/api.js';
import { adoptPairResponse } from '../lib/auth.js';
import AlphaNumPad from '../components/AlphaNumPad.jsx';

const CODE_LEN = 8;

export default function PairingScreen({ onPaired }) {
  const [code, setCode] = useState('');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (code.length < CODE_LEN) {
      setErr(`Code is ${CODE_LEN} characters`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await api.pair(code.trim().toUpperCase());
      // adoptPairResponse handles BOTH paths:
      //   - Phase 2 server returns wrap_key_half_a + signing_secret →
      //     handshake + seal + drop cleartext.
      //   - Legacy server returns just device_token → cleartext storage,
      //     no HMAC. Server side soft-allows.
      await adoptPairResponse(r);
      onPaired(r.device);
    } catch (e) {
      setErr(e.message || 'Pairing failed');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-6 py-10">
      <div className="w-full max-w-xl bg-white rounded-3xl shadow-xl p-8 space-y-6">
        <header>
          <h1 className="text-kiosk-lg font-semibold text-cathay-700">Pair this iPad</h1>
          <p className="text-kiosk text-slate-600 mt-2">
            Enter the 8-character code shown by your administrator when this device was created.
          </p>
        </header>

        <div className="bg-slate-100 rounded-2xl px-6 py-5 text-center font-mono tracking-[0.4em] text-kiosk-xl text-slate-900 min-h-[5rem] flex items-center justify-center">
          {code || <span className="text-slate-300">________</span>}
        </div>

        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-kiosk">
            {err}
          </div>
        )}

        <AlphaNumPad
          value={code}
          maxLen={CODE_LEN}
          onKey={(k) => setCode((c) => c + k)}
          onBackspace={() => setCode((c) => c.slice(0, -1))}
          onClear={() => setCode('')}
        />

        <button
          type="button"
          disabled={busy || code.length < CODE_LEN}
          onClick={submit}
          className="w-full bg-cathay-500 hover:bg-cathay-600 disabled:opacity-50 text-white text-kiosk-lg font-semibold py-5 rounded-2xl shadow"
        >
          {busy ? 'Pairing…' : 'Pair iPad'}
        </button>
      </div>
    </div>
  );
}
